import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDrag } from 'react-dnd'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import CircularProgress from '@mui/material/CircularProgress'
import configs from 'config/environment'
import { showImagePreview } from 'slices/customer'
import { buildVaultPreviewPayloadFromUnmasked } from 'utils/vaultPreviewSnapshot'

const Image = ({
  item,
  url,
  cursor = 'pointer',
  dragfalse = false,
  disablePreview = false,
  width = 150,
  height = 'auto',
  alt,
  className,
  style,
  shouldMask = false,
  cardId = null,
  rawUrl = false,
  refreshKey = null,
  imageIndex = 0,
  // haveOcr = false,
  ...props
}) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const currentTab = useSelector(state => state.customerSteps.currentTab)
  const canOpenPreview = !disablePreview && !isLoading
  const unmaskedCards = useSelector(
    state => state.customer.unmaskedCards || {}
  )
  const shouldApplyMask =
    shouldMask === true || (shouldMask !== false && cardId != null)

  const isImageReady = !!(
    url ||
    item?.document_revision_id ||
    item?.file_obj?.document_revision_id
  )

  const isCreditCard = !!cardId && isImageReady

  const isNewRelation =
    item?.new_relation_addition || item?.file_obj?.new_relation_addition || false

  const imageUrl = rawUrl
    ? url
    : `${configs.apiUrl}/index.php?entryPoint=Image&download=0&preview=0&mask=${shouldApplyMask ? 1 : 0
    }&file=${url}${refreshKey ? `&_t=${refreshKey}` : ''}`

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'image',
      item: { id: item },
      collect: monitor => ({
        isDragging: monitor.isDragging()
      })
    }),
    [item]
  )


  const handleImagePreview = e => {
    e.preventDefault()
    e.stopPropagation()

    // Block preview until the thumbnail has finished loading
    if (isLoading || disablePreview) return

    const fileName =
      item?.fileName ||
      item?.name ||
      item?.file_obj?.name ||
      item?.file_obj?.fileName ||
      'Unknown file'

    const docId =
      url ||
      item?.document_revision_id ||
      item?.file_obj?.document_revision_id ||
      item?.id

    const rawDocumentType =
      item?.gb_doc_type ||
      item?.file_obj?.gb_doc_type ||
      item?.documentType ||
      null
    const normalizedDocType = String(rawDocumentType || '').toLowerCase()
    const isUnassignedCreditCardType =
      !cardId &&
      (normalizedDocType === 'credit_card_image' ||
        normalizedDocType === 'credit_card')
    const documentType = isUnassignedCreditCardType ? null : rawDocumentType

    if (!fileName || !docId) {
      console.warn('Image preview: Missing fileName or url', {
        fileName,
        docId,
        item,
        url
      })
      return
    }

    const vaultPreviewPayload = cardId
      ? buildVaultPreviewPayloadFromUnmasked(unmaskedCards, cardId)
      : {
        isVaultSource: false,
        vaultImageUrl: null,
        vaultImages: null
      }

    dispatch(
      showImagePreview({
        name: fileName,
        docId: docId,
        preview: true,
        documentType,
        cardId: cardId,
        imageIndex,
        isNewRelation,
        ...vaultPreviewPayload
      })
    )
  }

  const handleDragStart = e => {
    if (dragfalse) {
      e.preventDefault()
      return
    }

    const dragImage = new window.Image()
    dragImage.src = imageUrl
    e.dataTransfer.setDragImage(dragImage, 75, 75)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleContextMenuImage = e => {
    e.preventDefault()
    e.stopPropagation()
    if (props.onContextMenu) {
      props.onContextMenu(e)
    }
    return false
  }

  const handleDragStartImage = e => {
    if (isCreditCard) {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
    if (props.onDragStart) {
      props.onDragStart(e)
    } else {
      handleDragStart(e)
    }
  }

  const handleDragEndImage = e => {
    if (props.onDragEnd) {
      props.onDragEnd(e)
    }
  }

  const handleSelectStart = e => {
    if (isCreditCard) {
      e.preventDefault()
      return false
    }
  }

  // Handle image load error
  const handleImageError = e => {
    console.warn(`Failed to load image: ${url}`)
    e.target.style.display = 'none'
    setIsLoading(false)
  }

  // Handle image load success
  const handleImageLoad = e => {
    e.target.style.display = 'block'
    setIsLoading(false)
  }

  return (
    <ImageContainer
      ref={drag}
      data-dragging={isDragging}
      className={className}
      isCreditCard={isCreditCard}
      onContextMenu={handleContextMenuImage}
      onDragStart={isCreditCard ? handleDragStartImage : undefined}
      onDragEnd={isCreditCard ? handleDragEndImage : undefined}
      onSelectStart={isCreditCard ? handleSelectStart : undefined}
      {...props}
    >
      {isLoading && currentTab === 2 && (
        <LoaderOverlay>
          <CircularProgress size={26} sx={{ color: '#f6c44b' }} />
        </LoaderOverlay>
      )}
      <StyledImage
        src={imageUrl}
        alt={
          alt ||
          item?.fileName ||
          item?.name ||
          item?.file_obj?.name ||
          'Image preview'
        }
        width={width}
        height={height}
        cursor={canOpenPreview ? cursor : 'default'}
        isDragging={isDragging}
        // shouldMask={shouldMask}
        onClick={disablePreview ? undefined : handleImagePreview}
        onContextMenu={handleContextMenuImage}
        onDragStart={isCreditCard ? handleDragStartImage : undefined}
        onDragEnd={isCreditCard ? handleDragEndImage : undefined}
        onError={handleImageError}
        onLoad={handleImageLoad}
        loading="lazy"
        draggable={false}
        role="img"
        aria-label={
          alt ||
          item?.fileName ||
          item?.name ||
          item?.file_obj?.name ||
          'Image preview'
        }
        tabIndex={canOpenPreview ? 0 : -1}
        style={style}
        onKeyDown={
          canOpenPreview
            ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleImagePreview(e)
              }
            }
            : undefined
        }
      />
    </ImageContainer>
  )
}

// PropTypes for better development experience
Image.propTypes = {
  item: PropTypes.shape({
    fileName: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  url: PropTypes.string.isRequired,
  cursor: PropTypes.string,
  dragfalse: PropTypes.bool,
  disablePreview: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  alt: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object
}

// Styled Components
const StyledImage = styled.img.withConfig({
  shouldForwardProp: prop =>
    !['isDragging', 'shouldMask', 'isCreditCard'].includes(prop)
})`
  width: ${props =>
    typeof props.width === 'number'
      ? `${props.width}px`
      : props.width || '150px'};
  height: ${props =>
    typeof props.height === 'number'
      ? `${props.height}px`
      : props.height || 'auto'};
  cursor: ${props => props.cursor};
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  transition: all 0.2s ease;
  object-fit: contain;
  object-position: center;
  /* filter: ${props => (props.shouldMask ? 'blur(5px)' : 'none')}; */

  /* Drag state styles */
  border: ${props => (props.isDragging ? '3px solid #28a745' : 'none')};
  opacity: ${props => (props.isDragging ? 0.6 : 1)};
  transform: ${props => (props.isDragging ? 'scale(0.95)' : 'scale(1)')};

  /* Loading state */
  &:not([src]) {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const ImageContainer = styled.div.withConfig({
  shouldForwardProp: prop => prop !== 'isCreditCard'
})`
  position: relative;
  display: inline-block;
  width: 100%;

  /* Drag preview styling */
  &[data-dragging='true'] {
    cursor: grabbing;
  }

  ${props =>
    props.isCreditCard &&
    `
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
    -webkit-touch-callout: none;
  `}
`

const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`


export default Image
